import { compareVersions, ProtocolVersion, KeyParamsOrigination } from '@standardnotes/common';
import { SessionEvent, ApplicationStage, FeaturesEvent, SyncMode, SyncSource, StoragePersistencePolicies, ApplicationEvent, ChallengeValidation, StorageKey, DeinitMode, DeinitSource, EncryptionServiceEvent, ErrorAlertStrings, ProtocolUpgradeStrings, SessionStrings, AccountEvent, InternalEventPublishStrategy, ApplicationOptionsDefaults, ProtectionEvent, PreferencesServiceEvent, } from '@standardnotes/services';
import { Environment, } from '@standardnotes/models';
import { nonSecureRandomIdentifier, assertUnreachable, removeFromArray, isNullOrUndefined, sleep, useBoolean, canBlockDeinit, } from '@standardnotes/utils';
import { applicationEventForSyncEvent } from '@Lib/Application/Event';
import { ComputePrivateUsername } from '@standardnotes/encryption';
import { SNLog } from '../Log';
import { Dependencies } from './Dependencies/Dependencies';
import { TYPES } from './Dependencies/Types';
import { RegisterApplicationServicesEvents } from './Dependencies/DependencyEvents';
export class SNApplication {
    constructor(options) {
        /**
         * A runtime based identifier for each dynamic instantiation of the application instance.
         * This differs from the persistent application.identifier which persists in storage
         * across instantiations.
         */
        this.ephemeralIdentifier = nonSecureRandomIdentifier();
        this.eventHandlers = [];
        this.serviceObservers = [];
        this.managedSubscribers = [];
        /** True if the result of deviceInterface.openDatabase yields a new database being created */
        this.createdNewDatabase = false;
        /** True if the application has started (but not necessarily launched) */
        this.started = false;
        /** True if the application has launched */
        this.launched = false;
        /** Whether the application has been destroyed via .deinit() */
        this.dealloced = false;
        this.revokingSession = false;
        this.handledFullSyncStage = false;
        const allOptions = {
            ...ApplicationOptionsDefaults,
            ...options,
        };
        if (!SNLog.onLog) {
            throw Error('SNLog.onLog must be set.');
        }
        if (!SNLog.onError) {
            throw Error('SNLog.onError must be set.');
        }
        const requiredOptions = [
            'deviceInterface',
            'environment',
            'platform',
            'crypto',
            'alertService',
            'identifier',
            'defaultHost',
            'appVersion',
            'apiVersion',
        ];
        for (const optionName of requiredOptions) {
            if (!allOptions[optionName]) {
                throw Error(`${optionName} must be supplied when creating an application.`);
            }
        }
        this.environment = options.environment;
        this.platform = options.platform;
        this.identifier = options.identifier;
        this.options = Object.freeze(allOptions);
        this.dependencies = new Dependencies(this.options);
        const logger = this.dependencies.get(TYPES.Logger);
        logger.setLevel('error');
        this.registerServiceObservers();
        RegisterApplicationServicesEvents(this.dependencies, this.events);
    }
    registerServiceObservers() {
        const encryptionService = this.dependencies.get(TYPES.EncryptionService);
        this.serviceObservers.push(encryptionService.addEventObserver(async (event) => {
            if (event === EncryptionServiceEvent.RootKeyStatusChanged) {
                await this.notifyEvent(ApplicationEvent.KeyStatusChanged);
            }
        }));
        this.dependencies.get(TYPES.DiskStorageService).provideEncryptionProvider(encryptionService);
        const apiService = this.dependencies.get(TYPES.LegacyApiService);
        this.dependencies
            .get(TYPES.HttpService)
            .setCallbacks(apiService.processMetaObject.bind(apiService), apiService.setSession.bind(apiService));
        this.serviceObservers.push(this.dependencies.get(TYPES.SessionManager).addEventObserver(async (event) => {
            switch (event) {
                case SessionEvent.Restored: {
                    void (async () => {
                        await this.sync.sync({ sourceDescription: 'Session restored pre key creation' });
                        if (encryptionService.needsNewRootKeyBasedItemsKey()) {
                            void encryptionService.createNewDefaultItemsKey().then(() => {
                                void this.sync.sync({ sourceDescription: 'Session restored post key creation' });
                            });
                        }
                    })();
                    break;
                }
                case SessionEvent.Revoked: {
                    await this.handleRevokedSession();
                    break;
                }
                case SessionEvent.UserKeyPairChanged:
                    break;
                default: {
                    assertUnreachable(event);
                }
            }
        }));
        const syncEventCallback = async (eventName, data) => {
            const appEvent = applicationEventForSyncEvent(eventName);
            if (appEvent) {
                await encryptionService.onSyncEvent(eventName);
                await this.notifyEvent(appEvent, data);
                if (appEvent === ApplicationEvent.CompletedFullSync) {
                    if (!this.handledFullSyncStage) {
                        this.handledFullSyncStage = true;
                        await this.handleStage(ApplicationStage.FullSyncCompleted_13);
                    }
                }
            }
        };
        const syncService = this.dependencies.get(TYPES.SyncService);
        const uninstall = syncService.addEventObserver(syncEventCallback);
        this.serviceObservers.push(uninstall);
        const protectionService = this.dependencies.get(TYPES.ProtectionService);
        this.serviceObservers.push(protectionService.addEventObserver((event) => {
            if (event === ProtectionEvent.UnprotectedSessionBegan) {
                void this.notifyEvent(ApplicationEvent.UnprotectedSessionBegan);
            }
            else if (event === ProtectionEvent.UnprotectedSessionExpired) {
                void this.notifyEvent(ApplicationEvent.UnprotectedSessionExpired);
            }
        }));
        const userService = this.dependencies.get(TYPES.UserService);
        this.serviceObservers.push(userService.addEventObserver(async (event, data) => {
            switch (event) {
                case AccountEvent.SignedInOrRegistered: {
                    void this.notifyEvent(ApplicationEvent.SignedIn);
                    break;
                }
                case AccountEvent.SignedOut: {
                    await this.notifyEvent(ApplicationEvent.SignedOut);
                    await this.prepareForDeinit();
                    this.deinit(this.getDeinitMode(), (data === null || data === void 0 ? void 0 : data.payload.source) || DeinitSource.SignOut);
                    break;
                }
                default: {
                    assertUnreachable(event);
                }
            }
        }));
        const preferencesService = this.dependencies.get(TYPES.PreferencesService);
        this.serviceObservers.push(preferencesService.addEventObserver((event) => {
            if (event === PreferencesServiceEvent.PreferencesChanged) {
                void this.notifyEvent(ApplicationEvent.PreferencesChanged);
            }
            else if (event === PreferencesServiceEvent.LocalPreferencesChanged) {
                void this.notifyEvent(ApplicationEvent.LocalPreferencesChanged);
            }
        }));
        const featuresService = this.dependencies.get(TYPES.FeaturesService);
        this.serviceObservers.push(featuresService.addEventObserver((event) => {
            switch (event) {
                case FeaturesEvent.UserRolesChanged: {
                    void this.notifyEvent(ApplicationEvent.UserRolesChanged);
                    break;
                }
                case FeaturesEvent.FeaturesAvailabilityChanged: {
                    void this.notifyEvent(ApplicationEvent.FeaturesAvailabilityChanged);
                    break;
                }
                case FeaturesEvent.DidPurchaseSubscription: {
                    void this.notifyEvent(ApplicationEvent.DidPurchaseSubscription);
                    break;
                }
                default: {
                    assertUnreachable(event);
                }
            }
        }));
    }
    computePrivateUsername(username) {
        return ComputePrivateUsername(this.options.crypto, username);
    }
    /**
     * The first thing consumers should call when starting their app.
     * This function will load all services in their correct order.
     */
    async prepareForLaunch(callback) {
        if (this.launched) {
            throw new Error('Attempting to prelaunch already launched application');
        }
        await this.options.crypto.initialize();
        this.setLaunchCallback(callback);
        const databaseResult = await this.device.openDatabase(this.identifier).catch((error) => {
            void this.notifyEvent(ApplicationEvent.LocalDatabaseReadError, error);
            return undefined;
        });
        this.createdNewDatabase = useBoolean(databaseResult === null || databaseResult === void 0 ? void 0 : databaseResult.isNewDatabase, false);
        await this.migrations.initialize();
        await this.notifyEvent(ApplicationEvent.MigrationsLoaded);
        await this.handleStage(ApplicationStage.PreparingForLaunch_0);
        await this.storage.initializeFromDisk();
        await this.notifyEvent(ApplicationEvent.StorageReady);
        await this.encryption.initialize();
        await this.handleStage(ApplicationStage.ReadyForLaunch_05);
        this.started = true;
        await this.notifyEvent(ApplicationEvent.Started);
    }
    setLaunchCallback(callback) {
        this.challenges.sendChallenge = callback.receiveChallenge;
    }
    /**
     * Handles device authentication, unlocks application, and
     * issues a callback if a device activation requires user input
     * (i.e local passcode or fingerprint).
     * @param awaitDatabaseLoad
     * Option to await database load before marking the app as ready.
     */
    async launch(awaitDatabaseLoad = false) {
        if (this.launched) {
            throw new Error('Attempting to launch already launched application');
        }
        this.launched = false;
        const launchChallenge = this.getLaunchChallenge();
        if (launchChallenge) {
            const response = await this.challenges.promptForChallengeResponse(launchChallenge);
            if (!response) {
                throw Error('Launch challenge was cancelled.');
            }
            await this.handleLaunchChallengeResponse(response);
        }
        if (this.storage.isStorageWrapped()) {
            try {
                await this.storage.decryptStorage();
            }
            catch {
                void this.alerts.alert(ErrorAlertStrings.StorageDecryptErrorBody, ErrorAlertStrings.StorageDecryptErrorTitle);
            }
        }
        await this.handleStage(ApplicationStage.StorageDecrypted_09);
        const host = this.legacyApi.loadHost();
        this.http.setHost(host);
        this.sockets.loadWebSocketUrl();
        this.settings.initializeFromDisk();
        this.launched = true;
        await this.notifyEvent(ApplicationEvent.Launched);
        await this.handleStage(ApplicationStage.Launched_10);
        await this.handleStage(ApplicationStage.LoadingDatabase_11);
        if (this.createdNewDatabase) {
            await this.sync.onNewDatabaseCreated();
        }
        /**
         * We don't want to await this, as we want to begin allowing the app to function
         * before local data has been loaded fully.
         */
        const loadPromise = this.sync
            .loadDatabasePayloads()
            .then(async () => {
            if (this.dealloced) {
                throw 'Application has been destroyed.';
            }
            await this.handleStage(ApplicationStage.LoadedDatabase_12);
            this.sync.beginAutoSyncTimer();
            await this.sync.sync({
                mode: SyncMode.DownloadFirst,
                source: SyncSource.External,
                sourceDescription: 'Application Launch',
            });
            this.vaultUsers.invalidateVaultUsersCache().catch(console.error);
        })
            .catch((error) => {
            void this.notifyEvent(ApplicationEvent.LocalDatabaseReadError, error);
            throw error;
        });
        if (awaitDatabaseLoad) {
            await loadPromise;
        }
    }
    onStart() {
        // optional override
    }
    onLaunch() {
        // optional override
    }
    getLaunchChallenge() {
        return this.protections.createLaunchChallenge();
    }
    async handleLaunchChallengeResponse(response) {
        var _a;
        if (response.challenge.hasPromptForValidationType(ChallengeValidation.LocalPasscode)) {
            let wrappingKey = (_a = response.artifacts) === null || _a === void 0 ? void 0 : _a.wrappingKey;
            if (!wrappingKey) {
                const value = response.getValueForType(ChallengeValidation.LocalPasscode);
                wrappingKey = await this.encryption.computeWrappingKey(value.value);
            }
            await this.encryption.unwrapRootKey(wrappingKey);
        }
    }
    async handleStage(stage) {
        await this.events.publishSync({
            type: ApplicationEvent.ApplicationStageChanged,
            payload: { stage },
        }, InternalEventPublishStrategy.SEQUENCE);
    }
    /**
     * @param singleEvent Whether to only listen for a particular event.
     */
    addEventObserver(callback, singleEvent) {
        const observer = { callback, singleEvent };
        this.eventHandlers.push(observer);
        return () => {
            removeFromArray(this.eventHandlers, observer);
        };
    }
    addSingleEventObserver(event, callback) {
        const filteredCallback = async (firedEvent) => {
            if (firedEvent === event) {
                void callback(event);
            }
        };
        return this.addEventObserver(filteredCallback, event);
    }
    async notifyEvent(event, data) {
        if (event === ApplicationEvent.Started) {
            this.onStart();
        }
        else if (event === ApplicationEvent.Launched) {
            this.onLaunch();
        }
        for (const observer of this.eventHandlers.slice()) {
            if ((observer.singleEvent && observer.singleEvent === event) || !observer.singleEvent) {
                await observer.callback(event, data || {});
            }
        }
        this.events.publish({
            type: event,
            payload: data,
        });
        void this.migrations.handleApplicationEvent(event);
    }
    getSessions() {
        return this.sessions.getSessionsList();
    }
    async revokeSession(sessionId) {
        if (await this.protections.authorizeSessionRevoking()) {
            return this.sessions.revokeSession(sessionId);
        }
        return undefined;
    }
    /**
     * Revokes all sessions except the current one.
     */
    async revokeAllOtherSessions() {
        return this.sessions.revokeAllOtherSessions();
    }
    userCanManageSessions() {
        const userVersion = this.getUserVersion();
        if (isNullOrUndefined(userVersion)) {
            return false;
        }
        return compareVersions(userVersion, ProtocolVersion.V004) >= 0;
    }
    async setCustomHost(host, websocketUrl) {
        await this.setHost.execute(host);
        this.sockets.setWebSocketUrl(websocketUrl);
    }
    getUserPasswordCreationDate() {
        return this.encryption.getPasswordCreatedDate();
    }
    getProtocolEncryptionDisplayName() {
        return this.encryption.getEncryptionDisplayName();
    }
    getUserVersion() {
        return this.encryption.getUserVersion();
    }
    /**
     * Returns true if there is an upgrade available for the account or passcode
     */
    protocolUpgradeAvailable() {
        return this.encryption.upgradeAvailable();
    }
    /**
     * Returns true if there is an encryption source available
     */
    isEncryptionAvailable() {
        return this.hasAccount() || this.hasPasscode();
    }
    async upgradeProtocolVersion() {
        const result = await this.user.performProtocolUpgrade();
        if (result.success) {
            if (this.hasAccount()) {
                void this.alerts.alert(ProtocolUpgradeStrings.SuccessAccount);
            }
            else {
                void this.alerts.alert(ProtocolUpgradeStrings.SuccessPasscodeOnly);
            }
        }
        else if (result.error) {
            void this.alerts.alert(ProtocolUpgradeStrings.Fail);
        }
        return result;
    }
    hasAccount() {
        return this.encryption.hasAccount();
    }
    /**
     * @returns true if the user has a source of protection available, such as a
     * passcode, password, or biometrics.
     */
    hasProtectionSources() {
        return this.protections.hasProtectionSources();
    }
    /**
     * When a user specifies a non-zero remember duration on a protection
     * challenge, a session will be started during which protections are disabled.
     */
    getProtectionSessionExpiryDate() {
        return this.protections.getSessionExpiryDate();
    }
    clearProtectionSession() {
        return this.protections.clearSession();
    }
    async authorizeProtectedActionForNotes(notes, challengeReason) {
        return await this.protections.authorizeProtectedActionForItems(notes, challengeReason);
    }
    /**
     * @returns whether note access has been granted or not
     */
    authorizeNoteAccess(note) {
        return this.protections.authorizeItemAccess(note);
    }
    authorizeAutolockIntervalChange() {
        return this.protections.authorizeAutolockIntervalChange();
    }
    isEphemeralSession() {
        return this.storage.isEphemeralSession();
    }
    setValue(key, value, mode) {
        return this.storage.setValue(key, value, mode);
    }
    getValue(key, mode) {
        return this.storage.getValue(key, mode);
    }
    async removeValue(key, mode) {
        return this.storage.removeValue(key, mode);
    }
    getPreference(key, defaultValue) {
        return this.preferences.getValue(key, defaultValue);
    }
    async setPreference(key, value) {
        return this.preferences.setValue(key, value);
    }
    /**
     * Gives services a chance to complete any sensitive operations before yielding
     * @param maxWait The maximum number of milliseconds to wait for services
     * to finish tasks. 0 means no limit.
     */
    async prepareForDeinit(maxWait = 0) {
        const deps = this.dependencies.getAll().filter(canBlockDeinit);
        const promise = Promise.all(deps.map((service) => service.blockDeinit()));
        if (maxWait === 0) {
            await promise;
        }
        else {
            /** Await up to maxWait. If not resolved by then, return. */
            await Promise.race([promise, sleep(maxWait, false, 'Preparing for deinit...')]);
        }
    }
    addChallengeObserver(challenge, observer) {
        return this.challenges.addChallengeObserver(challenge, observer);
    }
    submitValuesForChallenge(challenge, values) {
        return this.challenges.submitValuesForChallenge(challenge, values);
    }
    cancelChallenge(challenge) {
        this.challenges.cancelChallenge(challenge);
    }
    setOnDeinit(onDeinit) {
        this.onDeinit = onDeinit;
    }
    /**
     * Destroys the application instance.
     */
    deinit(mode, source) {
        var _a;
        this.dealloced = true;
        for (const uninstallObserver of this.serviceObservers) {
            uninstallObserver();
        }
        for (const uninstallSubscriber of this.managedSubscribers) {
            uninstallSubscriber();
        }
        this.options.crypto.deinit();
        this.options = undefined;
        this.createdNewDatabase = false;
        this.serviceObservers.length = 0;
        this.managedSubscribers.length = 0;
        this.started = false;
        this.dependencies.deinit();
        (_a = this.onDeinit) === null || _a === void 0 ? void 0 : _a.call(this, this, mode, source);
        this.onDeinit = undefined;
    }
    /**
     *  @param mergeLocal  Whether to merge existing offline data into account. If false,
     *                     any pre-existing data will be fully deleted upon success.
     */
    async register(email, password, hvmToken, ephemeral = false, mergeLocal = true) {
        return this.user.register(email, password, hvmToken, ephemeral, mergeLocal);
    }
    /**
     * @param mergeLocal  Whether to merge existing offline data into account.
     * If false, any pre-existing data will be fully deleted upon success.
     */
    async signIn(email, password, strict = false, ephemeral = false, mergeLocal = true, awaitSync = false, hvmToken) {
        return this.user.signIn(email, password, strict, ephemeral, mergeLocal, awaitSync, hvmToken);
    }
    async getCaptchaUrl() {
        return this.legacyApi.getCaptchaUrl();
    }
    async changeEmail(newEmail, currentPassword, passcode, origination = KeyParamsOrigination.EmailChange) {
        return this.user.changeCredentials({
            currentPassword,
            newEmail,
            passcode,
            origination,
            validateNewPasswordStrength: false,
        });
    }
    async changePassword(currentPassword, newPassword, passcode, origination = KeyParamsOrigination.PasswordChange, validateNewPasswordStrength = true) {
        return this.user.changeCredentials({
            currentPassword,
            newPassword,
            passcode,
            origination,
            validateNewPasswordStrength,
        });
    }
    async importData(data, awaitSync = false) {
        const usecase = this.dependencies.get(TYPES.ImportData);
        return usecase.execute(data, awaitSync);
    }
    async handleRevokedSession() {
        /**
         * Because multiple API requests can come back at the same time
         * indicating revoked session we only want to do this once.
         */
        if (this.revokingSession) {
            return;
        }
        this.revokingSession = true;
        /** Keep a reference to the soon-to-be-cleared alertService */
        const alertService = this.alerts;
        await this.user.signOut(true);
        void alertService.alert(SessionStrings.CurrentSessionRevoked);
    }
    async validateAccountPassword(password) {
        const { valid } = await this.encryption.validateAccountPassword(password);
        return valid;
    }
    isStarted() {
        return this.started;
    }
    isLaunched() {
        return this.launched;
    }
    hasPasscode() {
        return this.encryption.hasPasscode();
    }
    async lock() {
        /**
         * Because locking is a critical operation, we want to try to do it safely,
         * but only up to a certain limit.
         */
        const MaximumWaitTime = 500;
        await this.prepareForDeinit(MaximumWaitTime);
        return this.deinit(this.getDeinitMode(), DeinitSource.Lock);
    }
    isNativeMobileWeb() {
        return this.environment === Environment.Mobile;
    }
    getDeinitMode() {
        const value = this.getValue(StorageKey.DeinitMode);
        if (value === 'hard') {
            return DeinitMode.Hard;
        }
        return DeinitMode.Soft;
    }
    addPasscode(passcode) {
        return this.user.addPasscode(passcode);
    }
    /**
     * @returns whether the passcode was successfuly removed
     */
    async removePasscode() {
        return this.user.removePasscode();
    }
    async changePasscode(newPasscode, origination = KeyParamsOrigination.PasscodeChange) {
        return this.user.changePasscode(newPasscode, origination);
    }
    enableEphemeralPersistencePolicy() {
        return this.storage.setPersistencePolicy(StoragePersistencePolicies.Ephemeral);
    }
    hasPendingMigrations() {
        return this.migrations.hasPendingMigrations();
    }
    presentKeyRecoveryWizard() {
        const service = this.dependencies.get(TYPES.KeyRecoveryService);
        return service.presentKeyRecoveryWizard();
    }
    canAttemptDecryptionOfItem(item) {
        const service = this.dependencies.get(TYPES.KeyRecoveryService);
        return service.canAttemptDecryptionOfItem(item);
    }
    async isUsingHomeServer() {
        const homeServerService = this.dependencies.get(TYPES.HomeServerService);
        if (!homeServerService) {
            return false;
        }
        return this.getHost.execute().getValue() === (await homeServerService.getHomeServerUrl());
    }
    get device() {
        return this.dependencies.get(TYPES.DeviceInterface);
    }
    get subscriptions() {
        return this.dependencies.get(TYPES.SubscriptionManager);
    }
    get signInWithRecoveryCodes() {
        return this.dependencies.get(TYPES.SignInWithRecoveryCodes);
    }
    get getRecoveryCodes() {
        return this.dependencies.get(TYPES.GetRecoveryCodes);
    }
    get addAuthenticator() {
        return this.dependencies.get(TYPES.AddAuthenticator);
    }
    fetchAuthenticatorRegistrationOptions() {
        return this.dependencies.get(TYPES.AuthenticatorManager).generateRegistrationOptions();
    }
    get listAuthenticators() {
        return this.dependencies.get(TYPES.ListAuthenticators);
    }
    get deleteAuthenticator() {
        return this.dependencies.get(TYPES.DeleteAuthenticator);
    }
    get getAuthenticatorAuthenticationOptions() {
        return this.dependencies.get(TYPES.GetAuthenticatorAuthenticationOptions);
    }
    get getAuthenticatorAuthenticationResponse() {
        return this.dependencies.get(TYPES.GetAuthenticatorAuthenticationResponse);
    }
    get listRevisions() {
        return this.dependencies.get(TYPES.ListRevisions);
    }
    get getRevision() {
        return this.dependencies.get(TYPES.GetRevision);
    }
    get deleteRevision() {
        return this.dependencies.get(TYPES.DeleteRevision);
    }
    get files() {
        return this.dependencies.get(TYPES.FileService);
    }
    get features() {
        return this.dependencies.get(TYPES.FeaturesService);
    }
    get items() {
        return this.dependencies.get(TYPES.ItemManager);
    }
    get payloads() {
        return this.dependencies.get(TYPES.PayloadManager);
    }
    get protections() {
        return this.dependencies.get(TYPES.ProtectionService);
    }
    get sync() {
        return this.dependencies.get(TYPES.SyncService);
    }
    get user() {
        return this.dependencies.get(TYPES.UserService);
    }
    get settings() {
        return this.dependencies.get(TYPES.SettingsService);
    }
    get mutator() {
        return this.dependencies.get(TYPES.MutatorService);
    }
    get sessions() {
        return this.dependencies.get(TYPES.SessionManager);
    }
    get status() {
        return this.dependencies.get(TYPES.StatusService);
    }
    get fileBackups() {
        return this.dependencies.get(TYPES.FilesBackupService);
    }
    get componentManager() {
        return this.dependencies.get(TYPES.ComponentManager);
    }
    get listed() {
        return this.dependencies.get(TYPES.ListedService);
    }
    get alerts() {
        return this.dependencies.get(TYPES.AlertService);
    }
    get storage() {
        return this.dependencies.get(TYPES.DiskStorageService);
    }
    get actions() {
        return this.dependencies.get(TYPES.ActionsService);
    }
    get challenges() {
        return this.dependencies.get(TYPES.ChallengeService);
    }
    get asymmetric() {
        return this.dependencies.get(TYPES.AsymmetricMessageService);
    }
    get homeServer() {
        return this.dependencies.get(TYPES.HomeServerService);
    }
    get preferences() {
        return this.dependencies.get(TYPES.PreferencesService);
    }
    get history() {
        return this.dependencies.get(TYPES.HistoryManager);
    }
    get encryption() {
        return this.dependencies.get(TYPES.EncryptionService);
    }
    get events() {
        return this.dependencies.get(TYPES.InternalEventBus);
    }
    get vaults() {
        return this.dependencies.get(TYPES.VaultService);
    }
    get vaultLocks() {
        return this.dependencies.get(TYPES.VaultLockService);
    }
    get vaultUsers() {
        return this.dependencies.get(TYPES.VaultUserService);
    }
    get vaultInvites() {
        return this.dependencies.get(TYPES.VaultInviteService);
    }
    get contacts() {
        return this.dependencies.get(TYPES.ContactService);
    }
    get sharedVaults() {
        return this.dependencies.get(TYPES.SharedVaultService);
    }
    get changeAndSaveItem() {
        return this.dependencies.get(TYPES.ChangeAndSaveItem);
    }
    get getHost() {
        return this.dependencies.get(TYPES.GetHost);
    }
    get setHost() {
        return this.dependencies.get(TYPES.SetHost);
    }
    get legacyApi() {
        return this.dependencies.get(TYPES.LegacyApiService);
    }
    get mfa() {
        return this.dependencies.get(TYPES.MfaService);
    }
    get generateUuid() {
        return this.dependencies.get(TYPES.GenerateUuid);
    }
    get createDecryptedBackupFile() {
        return this.dependencies.get(TYPES.CreateDecryptedBackupFile);
    }
    get createEncryptedBackupFile() {
        return this.dependencies.get(TYPES.CreateEncryptedBackupFile);
    }
    get migrations() {
        return this.dependencies.get(TYPES.MigrationService);
    }
    get http() {
        return this.dependencies.get(TYPES.HttpService);
    }
    get sockets() {
        return this.dependencies.get(TYPES.WebSocketsService);
    }
}
